import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import { Trash2 } from 'lucide-react';

// Shared editor for stock take SOH (mode="stocktake") or order quantities (mode="order")
export const EditRecordDialog = ({ open, onOpenChange, record, mode, onSave, saving }) => {
  const [rows, setRows] = useState([]);
  const [notes, setNotes] = useState('');
  const isOrder = mode === 'order';

  useEffect(() => {
    if (!record) return;
    setRows((record.items || []).map((item, index) => ({
      index, item,
      value: String(isOrder ? (item.order_qty ?? 0) : (item.soh ?? 0)),
    })));
    setNotes(record.notes || '');
  }, [record, isOrder]);

  const updateRow = (index, value) => setRows((r) => r.map((row) => row.index === index ? { ...row, value } : row));
  const removeRow = (index) => setRows((r) => r.filter((row) => row.index !== index));

  const total = rows.reduce((sum, r) => sum + (parseFloat(r.value) || 0) * (r.item.cost_price || 0) / (r.item.order_unit_qty || 1), 0);

  const handleSave = () => {
    const items = rows.map((r) => isOrder
      ? { index: r.index, order_qty: parseFloat(r.value) || 0 }
      : { index: r.index, soh: parseFloat(r.value) || 0 });
    onSave(isOrder ? { items, notes } : { items });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid={`edit-${mode}-dialog`}>
        <DialogHeader>
          <DialogTitle>{isOrder ? 'Edit Order' : 'Edit Stock Take'}</DialogTitle>
          <DialogDescription>
            {record?.branch_name}{record?.supplier_name ? ` — ${record.supplier_name}` : ''}.
            {isOrder ? ' Change order quantities or remove items.' : ' Change counted stock on hand; order quantities are recalculated.'}
          </DialogDescription>
        </DialogHeader>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Item</TableHead>
              <TableHead className="text-center">PAR</TableHead>
              {isOrder && <TableHead className="text-center">SOH</TableHead>}
              <TableHead className="text-center">{isOrder ? 'Order' : 'SOH'}</TableHead>
              {!isOrder && <TableHead className="text-center">Order</TableHead>}
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              const par = r.item.par || 0;
              const liveOrder = Math.max(0, par - (parseFloat(r.value) || 0));
              return (
                <TableRow key={r.index} data-testid={`edit-row-${r.index}`}>
                  <TableCell>
                    <p className="font-medium">{r.item.item_name}</p>
                    <p className="text-xs text-muted-foreground">{r.item.unit}</p>
                  </TableCell>
                  <TableCell className="text-center">{par}</TableCell>
                  {isOrder && <TableCell className="text-center">{r.item.soh}</TableCell>}
                  <TableCell className="text-center">
                    <Input type="number" min="0" step="0.01" value={r.value}
                      onChange={(e) => updateRow(r.index, e.target.value)}
                      className="w-24 mx-auto text-center" data-testid={`edit-qty-input-${r.index}`} />
                  </TableCell>
                  {!isOrder && <TableCell className="text-center font-medium">{liveOrder}</TableCell>}
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => removeRow(r.index)} data-testid={`edit-remove-row-${r.index}`}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {isOrder && (
          <div className="space-y-2">
            <Label htmlFor="order-notes">Notes</Label>
            <Textarea id="order-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Supplier short on avos, sent 2 boxes" data-testid="edit-order-notes" />
            <p className="text-right font-semibold" data-testid="edit-order-total">Total: R{total.toFixed(2)}</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || rows.length === 0} data-testid={`save-${mode}-edit-btn`}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const EditedNote = ({ record }) => record?.edited_at ? (
  <p className="text-xs text-muted-foreground" data-testid="edited-note">
    Edited by {record.edited_by} on {new Date(record.edited_at).toLocaleString()}
  </p>
) : null;
